import { Component, h, Listen, Prop, State } from '@stencil/core';
import { FacetUpdateEvent, PaginationData } from '@hpfc/fc-frontend-core';
import { QUERY_INFO_CLASS } from './ResultsFoundConstants';

/**
 * Displays the number of search items found with pagination information.
 */
@Component({
  tag: 'fccd-results-found',
  styleUrl: 'results-found.scss',
})
export class ResultsFound {
  private PLACEHOLDER = '';
  /**
   * The text to display before the count of results found.
   * For example, `prefixText` + "1-10 of 100"
   */
  @Prop() prefixText: string = 'Showing doctors and clinicians';

  /**
   * Information about the search results, including the total number of results, results per page, and current page.
   */
  @State() paginationData: PaginationData;

  /**
   * The text to display, including the prefix text and the count of results
   */
  @State() resultsText: string;

  @Listen('fccdComponentEvent', { target: 'document' })
  updateHandler(event: CustomEvent<FacetUpdateEvent>) {
    if (event?.detail?.facetMap?.has('page')) {
      const pageData: PaginationData = event.detail.facetMap.get('page').value as PaginationData;
      // hold the current results count and page number to compare with the new data
      const previousResultsCount = this.paginationData?.totalCount;
      const previousPage = this.paginationData?.page;

      this.paginationData = pageData;

      // A11y fix: Force screen-readers to re-annouce text even if the text has not changed
      // set time out to allow the browser time to render the difference
      // and then reset the results text to the proper value

      // if the page & the total number of results don't change, we need to force the screen reader to re-announce the results
      if (previousResultsCount === this.paginationData?.totalCount && previousPage === this.paginationData?.page) {
        this.resultsText = this.PLACEHOLDER;
      } else {
        this.resultsText = this.getResultsTextFound();
      }
    }
  }

  componentDidUpdate() {
    if (this.resultsText === this.PLACEHOLDER && this.paginationData) {
      setTimeout(() => {
        this.resultsText = this.getResultsTextFound();
      }, 120);
    }
  }

  getResultsCountFound(): string {
    if (!this.paginationData) {
      return '';
    }
    const { totalCount, page, perPage } = this.paginationData;

    if (totalCount === 0) {
      return '';
    }

    return this.getResultsCount(totalCount, page, perPage);
  }

  private getResultsCount(totalCount: number, page: number, perPage: number): string {
    if (totalCount < perPage) {
      return `1-${totalCount} of ${totalCount}`;
    }

    const start = (page - 1) * perPage + 1;
    const end = start + perPage - 1;

    if (end > totalCount) {
      return `${start}-${totalCount} of ${totalCount}`;
    }

    return `${start}-${end} of ${totalCount}`;
  }

  getResultsTextFound(): string {
    if (!this.paginationData) {
      return '';
    }
    const { totalCount } = this.paginationData;

    if (totalCount === 0) {
      return "Sorry, we didn't find any results.";
    }
    return `${this.prefixText} ${this.getResultsCountFound()}`;
  }

  render() {
    return (
      <div aria-labelledby="resultsCount" role="region" class={QUERY_INFO_CLASS} tabindex="-1">
        <hp5-heading-decorator>
          <h2 id="resultsCount" aria-atomic="true" aria-live="polite" aria-relevant="text" class="wrap">
            {this.resultsText}
          </h2>
        </hp5-heading-decorator>
      </div>
    );
  }
}
